import React, { useState } from "react";
import {
  Grid,
  Typography,
  Card,
  Box,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import Button from "components/CustomButtons/Button";
import { getLabIdeas } from "views/APIs/APIUtility";
import { generateNotebookSpecificationsAPI, generateStreamlitSpecificationsAPI } from "views/APIs/APIUtility";

import NotificationContext from "views/Components/Context.js";
import MDEditor from "@uiw/react-md-editor";
import Alert from "views/Components/Alert";
import { _mapResourceToAppType } from "../CommonFunctions";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";

const useStyles = makeStyles({
  ...styles,
});

const AdditionalDetails = ({
  description,
  handleNavigation,
  lab,
  labID,
  files,
  instructions,
  setInstructions,
  setLoading,
  loading,
  handleSave,
  designType = "notebook",
}) => {
  const { showNotification } = React.useContext(NotificationContext);
  const [showAlert, setShowAlert] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);

  const classes = useStyles();

  const handleNext = async () => {
    // handleNavigation("Data Requirements");
    await handleSave(false);
    if (lab.status != "Lab Design") {
      setShowAlert(true);
    } else {
      setReviewDialog(true);
    }
  };

  const submitHandler = async () => {
    try {
      handleReviewDialogClose();
      const message = designType === "streamlit" 
        ? "Generating streamlit specifications..." 
        : "Generating notebook specifications...";
      setLoading({
        isLoading: true,
        message: message,
      });
      const formData = new FormData();
      formData.append("lab_id", labID);
      
      // Call the appropriate API based on designType
      if (designType === "streamlit") {
        await generateStreamlitSpecificationsAPI(formData);
      } else {
        await generateNotebookSpecificationsAPI(formData);
      }
      
      window.location.reload();
    } catch (error) {
      console.error("Error in handleNext:", error);
      showNotification("Error generating specifications", "danger");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleBack = () => {
    handleNavigation("Visualization Requirements");
  };

  const handleAdditionalDetailsChange = (event) => {
    setInstructions({ ...instructions, additionalDetails: event });
  };

  const handleConfirm = async () => {
    setShowAlert(false); // Close the alert
    setReviewDialog(true);
  };

  const handleCancel = () => {
    setShowAlert(false); // Close the alert
  };

  const handleReviewDialogClose = () => {
    setReviewDialog(false);
  };

  if (loading.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography
          variant="h6"
          style={{ textTransform: "none", marginLeft: 16 }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <Dialog
        open={reviewDialog}
        onClose={handleReviewDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h4">Review</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box data-color-mode="light" mt={0} p={0}>
            <Typography variant="h5" gutterBottom>
              The following requirements will be used as the basis for
              generating lab ideas.
            </Typography>

            {/* Learning Outcomes */}
            <Box mt={2}>
              <Typography
                variant="h6"
                style={{ textTransform: "none" }}
                gutterBottom
              >
                General Requirements
              </Typography>
              <MarkdownEditorWithPreview
                disabled={true}
                value={instructions.learningOutcomes}
              />
            </Box>

            {/* <Divider sx={{ my: 2 }} /> */}

            {/* Dataset Details */}
            {instructions.datasetType !== "No dataset" && (
              <Box mt={2}>
                <Typography
                  variant="h6"
                  style={{ textTransform: "none" }}
                  gutterBottom
                >
                  Data Requirements
                </Typography>
                <MarkdownEditorWithPreview
                  disabled={true}
                  value={instructions.datasetDetails}
                />
                {files.filter((file) => file.type === "Dataset").length > 0 ? (
                  <Box mt={1}>
                    <Typography variant="body1" color="textSecondary">
                      The following datasets are being used:
                    </Typography>
                    <Box component="ul" pl={2} mt={1}>
                      {files
                        .filter((file) => file.type === "Dataset")
                        .map((file, index) => (
                          <Box component="li" key={index} variant="body2">
                            {file.name}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            )}

            {/* <Divider sx={{ my: 2 }} /> */}

            {/* Visualization Details */}
            <Box mt={2}>
              <Typography
                variant="h6"
                style={{ textTransform: "none" }}
                gutterBottom
              >
                Visualization Requirements
              </Typography>
              <MarkdownEditorWithPreview
                disabled={true}
                value={instructions.visualizationDetails}
              />
              {files.filter((file) => file.type === "Image").length > 0 ? (
                <Box mt={1}>
                  <Typography variant="body1" color="textSecondary">
                    The following images are being used:
                  </Typography>
                  <Box component="ul" pl={2} mt={1}>
                    {files
                      .filter((file) => file.type === "Image")
                      .map((file, index) => (
                        <Box component="li" key={index} variant="body2">
                          {file.name}
                        </Box>
                      ))}
                  </Box>
                </Box>
              ) : (
                <></>
              )}
            </Box>

            {/* <Divider sx={{ my: 2 }} /> */}

            {/* Additional Details */}
            <Box mt={2}>
              <Typography
                variant="h6"
                style={{ textTransform: "none" }}
                gutterBottom
              >
                Additional Requirements
              </Typography>
              <MarkdownEditorWithPreview
                disabled={true}
                value={instructions.additionalDetails}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box width="100%" display="flex" justifyContent="space-between" p={2}>
            <Button onClick={handleReviewDialogClose} color="quCreateColor">
              Continue Editing
            </Button>
            <Button
              onClick={submitHandler}
              color="quCreateColor"
              variant="contained"
            >
              Next
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {showAlert && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        >
          Proceeding to the next step will reset your current progress in
          further steps. Your existing work will be overwritten, and new content
          will be created. Are you sure you want to continue?
        </Alert>
      )}

      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1" style={{ marginBottom: "10px" }}>
              {description}
            </Typography>
            <Divider style={{ marginBottom: "10px" }} />
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} sm={12}>
              <Card>
                <div data-color-mode="light">
                  <MarkdownEditorWithPreview
                    value={instructions.additionalDetails}
                    onChange={(event) => handleAdditionalDetailsChange(event)}
                    height={"300px"}
                  />
                </div>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid container style={{ marginTop: "10px" }}>
          <Grid item xs={12}>
            <Button
              color="quCreateColor"
              style={{ float: "right", fontWeight: "bold" }}
              endIcon={<NavigateNextIcon />}
              onClick={handleNext}
            >
              {designType === "streamlit" ? "Generate Streamlit Specifications" : "Generate Notebook Specifications"}
            </Button>
            <Button
              color="quCreateColor"
              style={{ float: "left", fontWeight: "bold" }}
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              color="quCreateColor"
              style={{ float: "right", fontWeight: "bold" }}
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </>
    </div>
  );
};

export default AdditionalDetails;
